import { Link } from '@tanstack/react-router';
import { WidgetContainer } from '@/components/analytics/WidgetContainer';
import type { ArticleAnalyticsData } from '@/types/analytics';
import styles from './TopArticles.module.scss';

export interface TopArticlesProps {
  data: ArticleAnalyticsData | null;
  isLoading?: boolean;
  error?: Error | null;
  limit?: number;
}

/**
 * Top Articles Widget
 *
 * Displays the top performing articles by view count.
 */
export function TopArticles({ data, isLoading, error, limit = 5 }: TopArticlesProps) {
  const isEmpty = !data || !data.topArticles || data.topArticles.length === 0;

  const sortedArticles = data?.topArticles
    ? [...data.topArticles].sort((a, b) => b.viewCount - a.viewCount).slice(0, limit)
    : [];

  return (
    <WidgetContainer
      title="Top Performing Articles"
      description="Articles with the most views"
      isLoading={isLoading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage="Create and share articles to see performance"
      size="large"
    >
      <div className={styles.topArticles}>
        {sortedArticles.map((article, index) => (
          <Link
            key={article.articleId}
            to="/articles/$articleId"
            params={{ articleId: article.articleId }}
            className={styles.topArticles__item}
          >
            <div className={styles.topArticles__rank}>#{index + 1}</div>
            <div className={styles.topArticles__info}>
              <div className={styles.topArticles__title}>{article.title}</div>
              <div className={styles.topArticles__stats}>
                <span className={styles.topArticles__views}>
                  {article.viewCount.toLocaleString()} views
                </span>
                <span className={styles.topArticles__separator}>·</span>
                <span className={styles.topArticles__comments}>
                  {article.commentCount} comments
                </span>
              </div>
            </div>
            <div className={styles.topArticles__viewBadge}>
              {article.viewCount >= 100 && '🔥'}
              {article.viewCount >= 50 && article.viewCount < 100 && '⭐'}
              {article.viewCount >= 10 && article.viewCount < 50 && '📈'}
            </div>
          </Link>
        ))}
      </div>
    </WidgetContainer>
  );
}
